import { DropdownListItem } from "@/lib/ts/dropdownListItemUtils";
import { get, put, post } from "../api.services";
import { PlatformDTO, PlateLayout } from "../commonModels";
import { ExperimentRelationshipsForSetupDTO } from "@/services/experimentRelationships/models";

export type ExperimentLike = {
    code: string;
    setupStatus: ExperimentSetupStatus;
};

export type ExperimentListDTO = {
    id: string; // ID in the discovery.experiments table
    benchlingId: string; // benchling_entry_id
    code: string; // experiment_code
    day0: string; // benchling day0, to be replaced by start_date
    experimentGroup: string; // experiment_group
    experimentGroupCode: string;
    experimentGroupName: string;
    name: string; // experiment_name
    invaioProject: string; // invaio_project
    investigator: string;
    isUsingMeasurements: boolean; // is_using_measurements
    protocolCode: string;
    protocolName: string;
    protocolId: string;
    qcStatus: string; // qc_status
    setupStatus: string;
    siteCountry: string; // site_country
    siteName: string; // site_name
    siteTimeZone: string; // site_time_zone
    status: string;
    studyDirector: string; // study_director
    displayName: string;
    startDate: string;
    alternativeIdentifier: string | null;
};

export type ExperimentMetadataField = {
    label: string;
    type: string;
    field: string;
    multiple?: boolean;
    options?: { label: string; value: string | boolean }[];
};

export type UpdateExperimentParams = {
    displayName: string;
    folderId?: string | null;
    alternativeIdentifier: string | null;
    startDate: string;
    isUsingMeasurements: boolean;
    experimentGroupId: string;
    status: ExperimentStatus | null;
    qcStatus: ExperimentQCStatus | null;
    containerTypeId?: string | null;
    numberOfReplicates?: number | null;
    plateLayout?: PlateLayout | null;
    metadata: Record<string, any> | null;
    relationships?: ExperimentRelationshipsForSetupDTO[] | null;
};

export interface ExperimentSetupResult {
    isValid: boolean;
    experimentCode?: string;
    notebookEntryUrl?: string;
    errors?: string[];
}

export enum ExperimentStatus {
    NOT_STARTED = "Not started",
    ONGOING = "Ongoing",
    COMPLETED = "Completed",
    CANCELED = "Canceled",
    BLOCKED = "Blocked",
}

export type ExperimentQCStatus = "Pass" | "Fail";
export type ExperimentSetupStatus = "Draft" | "Final";

export type MetadataConfigContext = "experiment" | "fermenterRun";

export const experimentsAPI = {
    getAllExperiments: async (): Promise<ExperimentListDTO[]> => {
        return get(`/experiments`);
    },
    getMetadataConfig: async (
        context: MetadataConfigContext,
        includeGeographicCoordinateFields: boolean
    ): Promise<ExperimentMetadataField[]> => {
        const contextQueryParam = `context=${context}`;
        const geoCoordinateFieldsQueryParam = `includeGeographicCoordinateFields=${includeGeographicCoordinateFields}`;
        return get(`/experiments/metadata?${contextQueryParam}&${geoCoordinateFieldsQueryParam}`);
    },
    getMetadataForExperiment: async (experimentCode: string): Promise<Record<string, any>> => {
        return get(`/experiments/${experimentCode}/metadata`);
    },
    updateExperiment: async (experimentCode: string, data: UpdateExperimentParams) => {
        return put(`/experiments/${experimentCode}`, data);
    },
    setupDraftExperiment: async (formData: FormData): Promise<ExperimentSetupResult> => {
        return post(`/experiments/setup/draft`, formData);
    },
    setupFinalizedExperiment: async (formData: FormData): Promise<ExperimentSetupResult> => {
        return post(`/experiments/setup/final`, formData);
    },
    finalizeDraftExperiment: async (experimentCode: string): Promise<ExperimentSetupResult> => {
        return post(`/experiments/${experimentCode}/setup/finalizeDraft`);
    },
    fetchPlatforms: async (): Promise<PlatformDTO[]> => {
        return get(`/experiments/platforms`);
    },
};

export function mapPlatformsToDropdownItems(
    platforms: PlatformDTO[]
): DropdownListItem<PlatformDTO>[] {
    return platforms.map((platform) => ({
        title: platform.name,
        value: {
            id: platform.id,
            name: platform.name,
            folderId: platform.folderId,
            experimentMethodsFolderId: platform.experimentMethodsFolderId,
        },
    }));
}
